import { useEffect, useState } from 'react';

import { useAuthUser } from 'auth/hooks';
import { Confirmation } from 'core/components';
import { IConfirmation } from 'core/types';
import { QuoteEntityType } from 'quotes/enums';
import { ICustomerQuote, ICustomerQuotePart } from 'quotes/types';

import { SubmitValidationWarning } from './SubmitValidationWarning';
import { ISubmitValidationResult } from './types';
import {
  getMinimumAmountWarning,
  getMissingPartNumberAndManufacturerWarning,
  getMissingPartNumberWarning,
  getSubmitValidationWarnings,
} from './utils';

interface IProps {
  quote: ICustomerQuote;
  onUpdateQuote: (quote: ICustomerQuote) => void;
  quoteEntityType: QuoteEntityType;
  parts: ICustomerQuotePart[];
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Cascading pre-submit confirmation chain for customer orders.
 * Mounted conditionally — runs validation on mount, shows the first applicable warning.
 * If none apply, calls onConfirm directly (opens the submit drawer).
 *
 * Chain order (each step gates the next):
 *
 * 1. Submit validation (alternate only) — blocking order + line-level errors
 *    (AR number, ISR, costs, prices, shipping, taxonomy, etc.)
 * 2. Minimum amount — order total below company threshold (proceed anyway?)
 * 3. Missing part information:
 *    - Non-alternate: lines without part number
 *    - Alternate: lines without part number + manufacturer
 */
export const SubmitCustomerOrderConfirmation = ({
  quote,
  onUpdateQuote,
  quoteEntityType,
  parts,
  onConfirm,
  onCancel,
}: IProps) => {
  const [validationWarnings, setValidationWarnings] = useState<ISubmitValidationResult | null>(
    null
  );
  const [activeConfirmation, setActiveConfirmation] = useState<IConfirmation | null>(null);

  const { user } = useAuthUser();

  const minimumOrderThreshold = Number(user?.companySettings?.minimumOrderThreshold);

  useEffect(() => {
    const submitValidationWarnings = getSubmitValidationWarnings(quote, parts);

    if (submitValidationWarnings) {
      setValidationWarnings(submitValidationWarnings);

      return;
    }

    setValidationWarnings(null);

    const isAlternateVersionEnabled = Boolean(quote.alternateVersionEnabled);

    const missingPartInfoWarning = isAlternateVersionEnabled
      ? getMissingPartNumberAndManufacturerWarning(parts, onConfirm, onCancel)
      : getMissingPartNumberWarning(parts, onConfirm, onCancel);

    const minimumAmountWarning = getMinimumAmountWarning(
      minimumOrderThreshold,
      parts,
      () => {
        if (missingPartInfoWarning) {
          setActiveConfirmation(missingPartInfoWarning);

          return;
        }

        onConfirm();
      },
      onCancel
    );

    if (minimumAmountWarning) {
      setActiveConfirmation(minimumAmountWarning);

      return;
    }

    if (missingPartInfoWarning) {
      setActiveConfirmation(missingPartInfoWarning);

      return;
    }

    onConfirm();
  }, [minimumOrderThreshold, onCancel, onConfirm, parts, quote]);

  if (validationWarnings) {
    return (
      <SubmitValidationWarning
        warnings={validationWarnings}
        quote={quote}
        onUpdateQuote={onUpdateQuote}
        quoteEntityType={quoteEntityType}
        onCancel={onCancel}
      />
    );
  }

  if (!activeConfirmation) {
    return null;
  }

  return <Confirmation isOpen={true} {...activeConfirmation} />;
};
